<template>
  <div
    class="message margin-bottom-0"
    style="--message-border-color: var(--fa-lt-gravy)"
  >
    <div
      class="display-flex align-between align-middle"
      style="gap: var(--spacing-md)"
    >
      <div
        class="display-flex align-between align-top"
        style="gap: var(--spacing-xl)"
      >
        <font-awesome-layers
          class="fa-2xl decorative"
          style="color: var(--fa-blue)"
        >
          <font-awesome-icon
            :icon="resourceIcon"
            style="opacity: 0.6"
          />
          <font-awesome-icon
            :icon="$v7.solid.faCircle"
            transform="shrink-3 down-6 right-8"
            style="color: var(--white)"
          />
          <font-awesome-icon
            :icon="$v7.solid.faCirclePlus"
            transform="shrink-6 down-6 right-8"
          />
        </font-awesome-layers>

        <div>
          <h3
            v-if="headline"
            class="h5"
          >
            {{ headline }}
          </h3>
          <p class="margin-y-0">
            {{ copy }}
          </p>
        </div>
      </div>
      <site-link
        :to="addOnRoute"
        class="button flat with-border margin-bottom-0"
        style="--border-color: var(--fa-blue); flex-shrink: 0"
        @click="$posthog.capture('add_on_option_click', { context: 'upsell_modal', feature: gate, resource })"
        >{{ linkText }}</site-link
      >
    </div>
  </div>
</template>

<script>
  import get from 'lodash/get'
  import { mapGetters } from 'vuex'

  import { addOnSectionRoute, isBinaryFor, proPlusPriceLabel, resourceMeta, unitAmount } from 'app/views/accounts/addOns/addOnResources'
  import { formatPriceCents } from 'app/views/plans/priceUtils'

  // Per-resource copy. Kept beside the component rather than in
  // `addOnResources.js` because the phrasing is specific to this surface — it
  // answers the plan cards above it ("just need more of the one thing?"),
  // where the shared module's `label` is a bare noun for section headings.
  //
  // `copy` is the unpriced fallback: the priced line ("Add 100 more for $5/yr")
  // needs the catalog, which only the preview endpoint can speak for.
  // There's no headline when the add-on is the modal's only offer — the modal's
  // own heading already states it, and there are no plan cards to answer.
  //
  // The mark is NOT here: `ADD_ON_RESOURCES[].iconName` owns it, so the icon a
  // customer sees on this offer is the one the add-ons page shows them next.
  const COPY = {
    kits: {
      headline: 'Just need more Kits?',
      copy: 'Add more to your current plan.',
      primaryCopy: 'Buy extra Kits without changing your plan.',
      linkText: 'Add Kits'
    },
    icon_uploads: {
      headline: 'Just need more Custom Icons?',
      copy: 'Add more to your current plan.',
      primaryCopy: 'Buy extra Custom Icons without changing your plan.',
      linkText: 'Add Custom Icons'
    },
    pro_plus: {
      headline: 'Just want Pro+ icons?',
      copy: 'Add to your current plan.',
      primaryCopy: 'Get all the Pro+ icons without changing your plan.',
      linkText: 'Add Pro+ Icons'
    }
  }

  // The plan interval the preview priced against, abbreviated for a line that
  // has to stay short next to the button.
  const INTERVAL_SUFFIX = {
    year: 'yr',
    month: 'mo'
  }

  export default {
    name: 'UpsellAddOnOption',

    props: {
      // An `add_on_products` resource key — see `GATE_ADD_ON_RESOURCES` in
      // upsellPolicy.js for the gates that map here.
      resource: {
        type: String,
        required: true,
        validator: (value) => Object.keys(COPY).includes(value)
      },
      // Carried into the PostHog event so the click can be attributed to the
      // gate the customer actually hit.
      gate: {
        type: String,
        required: true
      },
      // One pack, priced by the server. The modal fetches it because whether
      // another pack fits decides what the modal shows. Null until it lands,
      // and on failure.
      preview: {
        type: Object,
        default: null
      },
      // The add-on is the modal's only offer, not an alternative to plan cards.
      primary: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      ...mapGetters('subscriptions', ['currentSubscription']),

      // `from` tags the purchase with where it started.
      addOnRoute() {
        const route = addOnSectionRoute(this.resource)
        return { ...route, query: { ...route.query, from: 'upsell' } }
      },

      headline() {
        return this.primary ? null : COPY[this.resource].headline
      },

      resourceIcon() {
        return get(this.$v7, ['solid', get(resourceMeta(this.resource), 'iconName', '')], null)
      },

      // "Add 100 more for $5/yr" — pack size and price both come from the
      // `add_on_products` catalog via the preview, so a catalog change moves
      // this copy without a deploy. Falls back to the unpriced sentence until
      // the preview lands (and if it never does), rather than showing a figure
      // this surface guessed at.
      copy() {
        if (isBinaryFor(this.resource)) return this.binaryCopy

        const incrementSize = get(this.preview, 'incrementSize', null)
        const suffix = INTERVAL_SUFFIX[get(this.preview, 'interval', null)]

        if (!incrementSize || !suffix) return this.primary ? COPY[this.resource].primaryCopy : COPY[this.resource].copy

        const price = formatPriceCents(get(this.preview, 'unitPriceCents', 0))
        return `Add ${unitAmount(this.resource, incrementSize)} more for ${price}/${suffix}.`
      },

      // An on/off add-on has no pack to count, and is priced by the customer's
      // plan rather than the catalog: "Add to your current plan for $30/yr."
      binaryCopy() {
        const price = proPlusPriceLabel(this.currentSubscription)
        const base = this.primary ? COPY[this.resource].primaryCopy : COPY[this.resource].copy

        return price ? `${base.slice(0, -1)} for ${price}.` : base
      },

      linkText() {
        return COPY[this.resource].linkText
      }
    }
  }
</script>
